import { useState } from 'react'

import { Tag } from '@hhgtech/hhg-components'
import { Button, Input, Text } from '@hhgtech/hhg-components/mantine'
import { Plus } from '@hhgtech/icons/core'
import { Badge, Box, BoxProps } from '@mantine/core'
import { useIntl } from 'react-intl'

import Cart from 'components/atoms/icons/Cart'
import Minus from 'components/atoms/icons/Minus'
import { domainLocales } from 'domainLocales'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { Product } from 'interfaces/types/Ecommerce'
import { currencyFormat } from 'utils/ecommerce'

import useStyles from './index.styles'
import { ProductImageCarousel } from './productImageCarousel'
import { BuyNowButtons } from '../buyNowButtons'

type Props = {
  product: Product
  onAddToCart: (amount: number, sku: number | string) => void
  onBuyNow: (amount: number, sku: number | string, slug: string) => void
  cartBtnColor?: string
} & BoxProps

const ProductWideCard = ({
  product,
  styles,
  className,
  classNames,
  onAddToCart,
  onBuyNow,
  cartBtnColor,
  ...boxProps
}: Props) => {
  const { locale } = useRouterExtend()
  const [loading, setLoading] = useState(false)
  const [buyLoading, setBuyLoading] = useState(false)
  const { formatMessage: f } = useIntl()
  const isMobile = useIsMobile()
  const { name, images: imagesProp = [], sku, slug, price, isInStock } = product
  const images = imagesProp.map((i) => i.medium)
  const { classes, cx } = useStyles(undefined, {
    name: 'ProductWideCard',
    styles,
    classNames,
  })
  const [amount, setAmount] = useState<number>(1)
  const url = `/shop/san-pham/${slug}/`
  const trackingLabel = `https://${domainLocales[locale]}${url}`

  return (
    <Box className={cx(classes.root, className)} {...boxProps}>
      <Box
        className={classes.slider}
        data-event-category="Ecommerce"
        data-event-action="Open Product Page"
        data-event-label={trackingLabel}
      >
        <ProductImageCarousel
          url={url}
          images={images}
          hideSeemore={true}
          hideBottomScrollList={isMobile}
          productName={name}
        />
      </Box>
      <Box className={classes.info}>
        <Box className={classes.mainInfo}>
          <Box
            component="a"
            href={url}
            target="_blank"
            data-event-category="Ecommerce"
            data-event-action="Open Product Page"
            data-event-label={trackingLabel}
          >
            <Text size="s3" className={classes.name}>
              {name}
            </Text>
          </Box>
          <Box className={classes.prices}>
            <Text size="s1" className={classes.mainPrice}>
              {currencyFormat(price.final, locale, 'VND')}
            </Text>
            {price.regular && price.regular !== price.final ? (
              <Text size="s3" className={classes.originPrice}>
                {currencyFormat(price.regular, locale, 'VND')}
              </Text>
            ) : null}
            {price.discountPercent ? (
              <Badge
                size="md"
                color="red"
                variant="filled"
                sx={{
                  paddingLeft: '5px',
                  paddingRight: '5px',
                  height: '22px',
                }}
              >
                -
                {Math.round(price.discountPercent) === 0
                  ? Math.ceil(price.discountPercent)
                  : Math.round(price.discountPercent)}
                %
              </Badge>
            ) : null}
          </Box>
          {!product.isAffiliateProduct && (
            <Box className={classes.actionRows}>
              <Box className={classes.actionRow}>
                <Box className={cx(classes.actionCol, classes.actionLabel)}>
                  <Text size="p3">{f({ id: 'productInfo.amount' })}</Text>
                </Box>
                <Box className={classes.actionCol}>
                  {isInStock ? (
                    <Box className={classes.amountControl}>
                      <Button
                        variant="tertiary"
                        className={classes.amountBtn}
                        leftIcon={<Minus color="#595959" size={18} />}
                        onClick={() => {
                          setAmount(Math.max(amount - 1, 1))
                        }}
                        size="sm"
                        color="secondary"
                      />
                      <Input.Number
                        className={classes.amountInput}
                        size="sm"
                        value={amount}
                      />
                      <Button
                        variant="tertiary"
                        className={classes.amountBtn}
                        leftIcon={<Plus color="#595959" size={18} />}
                        onClick={() => {
                          setAmount(Math.min(amount + 1, 999))
                        }}
                        size="sm"
                      />
                    </Box>
                  ) : null}
                </Box>
              </Box>
              {product.productPromotions.length > 0 && (
                <Box className={classes.actionRow}>
                  <Box className={cx(classes.actionCol, classes.actionLabel)}>
                    <Text size="p3" className={classes.actionLabel}>
                      {f({ id: 'productInfo.promotion' })}
                    </Text>
                  </Box>
                  <Box className={classes.actionCol}>
                    <Box className={classes.promotionTags}>
                      {product.productPromotions.map((promotion, index) => (
                        <Tag
                          key={index}
                          type="red"
                          isLight
                          isInline
                          isSquared
                          style={{
                            color: 'red',
                          }}
                        >
                          <Text
                            size="n1"
                            sx={(theme) => ({
                              color: `${theme.colors.red[7]}!important`,
                            })}
                          >
                            {promotion.name}
                          </Text>
                        </Tag>
                      ))}
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Box>
        <Box className={classes.actions}>
          {product.isAffiliateProduct ? (
            <Box
              sx={{
                width: '100%',
              }}
            >
              <BuyNowButtons affiliates={product.affiliates || []} />
            </Box>
          ) : (
            <>
              <Button
                variant="light"
                color={cartBtnColor}
                className={classes.cartBtn}
                loading={loading}
                leftIcon={<Cart size={18} color="currentColor" />}
                data-event-category="Ecommerce"
                data-event-action="Add To Cart Click"
                data-event-label={trackingLabel}
                onClick={async () => {
                  setLoading(true)
                  await onAddToCart?.(amount, sku)
                  setAmount(1)
                  setLoading(false)
                }}
                disabled={!isInStock}
              ></Button>
              <Button
                loading={buyLoading}
                className={classes.buyBtn}
                data-event-category="Ecommerce"
                data-event-action="Buy Now Click"
                data-event-label={trackingLabel}
                onClick={async () => {
                  setBuyLoading(true)
                  await onBuyNow?.(amount, sku, slug)
                  setAmount(1)
                  setBuyLoading(false)
                }}
                disabled={!isInStock}
              >
                {isInStock
                  ? f({
                      id: 'productInfo.buyNow',
                      defaultMessage: 'Mua ngay',
                    })
                  : f({
                      id: 'tag.outOfStock',
                      defaultMessage: 'Hết hàng',
                    })}
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export { ProductWideCard }
